import React from 'react';
import { Dropdown, Menu } from 'antd';
import { Button, Icon } from '@common/components';
import { useNavigate } from '@common/hooks';
import { datetime } from '@common/services';

// A selectable scheduling period for the menu (the dashboard's `filterOptions.periods`
// shape — org-level, so the same set applies to every service-line group).
type TeamReviewPeriod = {
  id: string;
  periodStart: string;
  periodEnd: string;
};

// Flag-gated ("team_review_period_picker") replacement for the group row's single
// "Review Team" CTA: an always-visible button whose menu lists the service line's
// current + future scheduling periods, each deep-linking to that period's Team Review
// page. Lets QA reach the page for ANY period rather than the auto-derived
// "earliest awaiting review" one. When no period is open (none current/future) the
// button is disabled. Mirrors the Dropdown + Menu pattern in _scheduling-period-actions.
//
// TODO(WORK-6503): this whole component is debug-only — remove it (and its callsite in
// schedule-periods-table) when the debug Review Team behavior is torn down.
// https://app.asana.com/1/303134204339906/project/1213809590907449/task/1216012235109932
export function TeamReviewPeriodMenu({
  serviceLineId,
  periods,
}: {
  serviceLineId: string;
  periods: TeamReviewPeriod[];
}) {
  const navigate = useNavigate();

  function label(period: TeamReviewPeriod): string {
    return `${datetime(period.periodStart).format('MMM D')} – ${datetime(
      period.periodEnd
    ).format('MMM D, YYYY')}`;
  }

  if (periods.length === 0) {
    return (
      <Button kind="primary" size="sm" disabled>
        Review Team
      </Button>
    );
  }

  const periodsMenu = (
    <Menu>
      {periods.map((period) => (
        <Menu.Item
          key={period.id}
          onClick={({ domEvent }) => {
            // The group row's onClick toggles expand; keep menu navigation from also toggling.
            domEvent.stopPropagation();
            navigate(
              `/works/schedule/dashboard/${serviceLineId}/team-review/${period.id}`
            );
          }}
        >
          {label(period)}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Dropdown
      overlay={periodsMenu}
      trigger={['click']}
      getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
    >
      <Button
        kind="primary"
        size="sm"
        iconRight={<Icon icon="chevronDown" kind="regular" />}
        // Opening the menu must not toggle the group row's expand state.
        onClick={(e) => e.stopPropagation()}
      >
        Review Team
      </Button>
    </Dropdown>
  );
}
